import React, { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Upload, Download, Star, Loader2, Flower2, Sparkles } from "lucide-react";

// Achiever Card: member uploads a photo, we render a congratulatory Star Club card
// showing their photo, name, user ID and the achieved tier, and lets them download a PNG.
export const AchieverCard = ({ name, userId, club }) => {
  const cardRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhoto(ev.target.result);
    reader.readAsDataURL(f);
  };

  const download = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2.5, cacheBust: true });
      const link = document.createElement("a");
      link.download = `star-club-${club}-${userId || "member"}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.warn("Card export failed:", err?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="achiever-card">
      {/* The exportable card */}
      <div className="flex justify-center">
        <div
          ref={cardRef}
          className="relative w-[380px] h-[480px] overflow-hidden rounded-2xl"
          style={{
            background: "radial-gradient(120% 90% at 50% 0%, #1c1a12 0%, #0b0b0d 55%, #050506 100%)",
            border: "2px solid #D4AF37",
            boxShadow: "0 0 0 1px rgba(212,175,55,0.25) inset",
          }}
        >
          {/* corner shimmer */}
          <div style={{ position: "absolute", top: -60, right: -60, width: 180, height: 180, background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)" }} />
          <div style={{ position: "absolute", bottom: -60, left: -60, width: 180, height: 180, background: "radial-gradient(circle, rgba(212,175,55,0.20), transparent 70%)" }} />
          {/* congratulations flowers / sparkles */}
          <Flower2 size={30} color="#D4AF37" style={{ position: "absolute", top: 12, left: 12, opacity: 0.65 }} />
          <Flower2 size={30} color="#D4AF37" style={{ position: "absolute", top: 12, right: 12, opacity: 0.65, transform: "scaleX(-1)" }} />
          <Sparkles size={18} color="#F5E6B3" style={{ position: "absolute", top: 70, left: 26, opacity: 0.7 }} />
          <Sparkles size={16} color="#F5E6B3" style={{ position: "absolute", top: 90, right: 30, opacity: 0.6 }} />
          <Flower2 size={22} color="#8a6d1a" style={{ position: "absolute", bottom: 60, left: 18, opacity: 0.55 }} />
          <Flower2 size={22} color="#8a6d1a" style={{ position: "absolute", bottom: 60, right: 18, opacity: 0.55, transform: "scaleX(-1)" }} />

          <div className="relative flex flex-col items-center px-6 pt-6 pb-5 h-full">
            <div style={{ letterSpacing: "0.35em", fontSize: 10, color: "#D4AF37" }}>LT GLOBAL CLUB</div>
            <div
              style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 34, lineHeight: 1.05, color: "#F5E6B3", marginTop: 6, textShadow: "0 1px 8px rgba(212,175,55,0.35)" }}
            >
              Congratulations
            </div>

            {/* Photo with gold ring */}
            <div style={{ marginTop: 18, position: "relative" }}>
              <div
                style={{
                  width: 150, height: 150, borderRadius: "50%",
                  padding: 4,
                  background: "linear-gradient(135deg,#f6d365,#D4AF37,#8a6d1a)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                }}
              >
                <div style={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", background: "#111", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {photo ? (
                    <img src={photo} alt="achiever" style={{ width: "100%", height: "100%", objectFit: "cover" }} crossOrigin="anonymous" />
                  ) : (
                    <div style={{ color: "#555", fontSize: 11, textAlign: "center", padding: 10 }}>Upload your<br />photo</div>
                  )}
                </div>
              </div>
              <div style={{ position: "absolute", bottom: -12, left: "50%", transform: "translateX(-50%)", background: "#D4AF37", color: "#1a1500", borderRadius: 22, padding: "4px 14px", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                <Star size={16} fill="#1a1500" /> STAR CLUB {club}
              </div>
            </div>

            {/* Big Star + Star Club title (congratulations, no payout) */}
            <div style={{ marginTop: 30, textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
                <Flower2 size={22} color="#D4AF37" />
                <Star size={58} color="#D4AF37" fill="#D4AF37" style={{ filter: "drop-shadow(0 0 14px rgba(212,175,55,0.75))" }} />
                <Flower2 size={22} color="#D4AF37" style={{ transform: "scaleX(-1)" }} />
              </div>
              <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 26, color: "#F5E6B3", marginTop: 8, letterSpacing: "0.04em" }}>
                Star Club {club}
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 4, color: "#9a8a4a" }}>
                <Sparkles size={12} /><span style={{ fontSize: 10, letterSpacing: "0.25em" }}>ACHIEVEMENT UNLOCKED</span><Sparkles size={12} />
              </div>
            </div>

            {/* Name + ID */}
            <div style={{ marginTop: "auto", textAlign: "center", width: "100%" }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.05em" }} data-testid="achiever-name">
                {name || "Member"}
              </div>
              <div style={{ fontSize: 13, color: "#D4AF37", marginTop: 2, letterSpacing: "0.15em" }} data-testid="achiever-id">ID · {userId || "—"}</div>
              <div style={{ borderTop: "1px solid rgba(212,175,55,0.25)", marginTop: 12, paddingTop: 8, fontSize: 9, letterSpacing: "0.25em", color: "#6b6250" }}>
                L T G L O B A L C L U B
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <label className="cursor-pointer inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 text-sm hover:bg-[#D4AF37]/10 transition-colors" data-testid="achiever-upload-label">
          <Upload size={15} /> {photo ? "Change photo" : "Upload photo"}
          <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="achiever-upload-input" />
        </label>
        <button
          className="lt-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          onClick={download}
          disabled={busy || !photo}
          data-testid="achiever-download-btn"
        >
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download card
        </button>
      </div>
      {!photo && <div className="text-xs text-neutral-500 text-center">Upload a photo from your gallery to enable download.</div>}
    </div>
  );
};

export default AchieverCard;
